// `/flowk/:id` (7.2 M1): one flow, step by step. The trigger and the flow
// filter in words, then the steps in order — a split's Igen and Nem ways
// indented under it, a wait in words, an A/B test with its variations and
// their shares, a letter with its subject, sender, template and smart sending —
// and whatever was not understood in red, with what Klaviyo sent. Older
// versions are one pick away. The canvas, the editor and the versions side by
// side are a click away (7.3 M7).

import { useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { GitCompareArrowsIcon, PencilIcon, ScaleIcon, WorkflowIcon } from "lucide-react";
import { ApiError, fetchFlow, fetchFlowVersion, fetchFlowVersions, startFlowDraft } from "@/lib/api";
import type {
  FlowDefinition,
  FlowDetail as FlowDetailData,
  FlowMessage,
  FlowStep,
  FlowUnsupported,
  FlowVersionDetail,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLoad } from "@/lib/useLoad";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { NativeSelect } from "@/components/ui/native-select";
import { DetailList } from "@/components/emc/DetailList";
import { Field } from "@/components/emc/Field";
import { Country } from "@/components/emc/Flag";
import { PageHeader } from "@/components/emc/PageHeader";
import { StatusPill } from "@/components/emc/StatusPill";
import { Callout, EmptyState, ErrorState, LoadingState } from "@/components/emc/states";
import { minute } from "@/components/emc/format";
import { statusLook } from "@/components/emc/status";
import { cn } from "@/lib/utils";
import { ChangeMark, SupportMark } from "@/pages/flows/FlowList";
import { FlowTemplatesProvider, KlaviyoTemplateOf } from "@/pages/flows/KlaviyoTemplate";
import {
  conditionGroups,
  conditionText,
  delayText,
  isUnsupported,
  outline,
  reentrySentence,
  triggerSentence,
  weightText,
} from "@/pages/flows/model";
import type { OutlineNode } from "@/pages/flows/model";
import { FlowShadow, FlowShadowCard, StepShadowBadge } from "@/pages/flows/FlowShadow";

function RawJson({ value }: { value: unknown }) {
  return (
    <pre className="data-mono m-0 mt-2 max-h-80 overflow-auto rounded-control border border-line bg-surface p-3 text-label whitespace-pre-wrap break-words">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

/** Something Klaviyo sent that we do not understand: red, and shown as it came. */
function Unsupported({ what, value }: { what: string; value: FlowUnsupported }) {
  return (
    <div className="rounded-inner bg-red-soft px-3 py-2">
      <p className="m-0 font-semibold text-red-ink">Nem támogatott {what}</p>
      <RawJson value={value.raw} />
    </div>
  );
}

function StepCard({ title, children, tone }: { title: ReactNode; children?: ReactNode; tone?: "danger" }) {
  return (
    <div
      className={cn(
        "rounded-inner border border-line bg-card px-3.5 py-2.5",
        tone === "danger" && "border-destructive",
      )}
    >
      <div className="flex flex-wrap items-center gap-2 font-medium">{title}</div>
      {children && <div className="mt-1.5 flex flex-col gap-1 text-detail text-muted-foreground">{children}</div>}
    </div>
  );
}

function Message({ message }: { message: FlowMessage | FlowUnsupported }) {
  if (isUnsupported(message)) return <Unsupported what="levél" value={message} />;
  const filter = message.filter === null ? null : conditionText(message.filter);
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-foreground">
        <span className="font-medium">{message.subject || "(nincs tárgy)"}</span>
        {message.preheader ? <span className="text-muted-foreground"> — {message.preheader}</span> : null}
      </span>
      <span>
        Feladó: {message.from_label ?? "—"} &lt;{message.from_email ?? "—"}&gt;
        {message.reply_to ? ` · válaszcím: ${message.reply_to}` : ""}
      </span>
      <span>
        Sablon: <span className="data-mono">{message.template_source_id ?? "—"}</span>{" "}
        <KlaviyoTemplateOf sourceId={message.template_source_id} />
        {" · "}
        Smart sending: {message.smart_sending ? "be" : "ki"}
        {message.transactional ? " · tranzakciós" : ""}
        {" · "}
        Levél-azonosító: <span className="data-mono">{message.source_id}</span>
        {message.name ? ` (${message.name})` : ""}
      </span>
      {filter && <span>Csak ha: {filter}</span>}
    </div>
  );
}

function StatusIfNotLive({ status }: { status: string }) {
  return status === "live" ? null : <StatusPill domain="flowSource" state={status} />;
}

function Step({ id, step }: { id: string; step: FlowStep }) {
  const tag = <><span className="data-mono text-label text-muted-foreground">#{id}</span><StepShadowBadge id={id} /></>;
  if (isUnsupported(step)) {
    return (
      <StepCard tone="danger" title={<>Nem támogatott lépés {tag}</>}>
        <Unsupported what="lépés" value={step} />
      </StepCard>
    );
  }
  switch (step.kind) {
    case "delay":
      return <StepCard title={<>Várakozás: {delayText(step)} {tag}</>} />;
    case "email":
      return (
        <StepCard title={<>Levél {tag} <StatusIfNotLive status={step.status} /></>}>
          <Message message={step.message} />
        </StepCard>
      );
    case "ab":
      return (
        <StepCard
          title={
            <>
              A/B teszt {tag} <StatusPill domain="flowExperiment" state={step.experiment} />
              <StatusIfNotLive status={step.status} />
            </>
          }
        >
          <div>
            <p className="m-0 font-medium text-foreground">
              Fő levél{step.experiment === "live" ? "" : " — ezt kapja mindenki"}
            </p>
            <Message message={step.main} />
          </div>
          {step.variants.map((variant, index) => (
            <div key={variant.id} className="border-t border-line pt-1.5">
              <p className="m-0 font-medium text-foreground">
                {index + 1}. változat · {weightText(variant.weight)}{" "}
                <span className="data-mono text-label text-muted-foreground">#{variant.id}</span>
              </p>
              <Message message={variant.message} />
            </div>
          ))}
        </StepCard>
      );
    case "noop":
      return (
        <StepCard title={<>Klaviyo-lépés ({step.klaviyo_type}) {tag} <StatusPill domain="flowSource" state={step.status} /></>}>
          <span>Vázlat: a Klaviyóban sem csinál semmit.</span>
        </StepCard>
      );
    case "split":
    case "multi_split":
      return null;
  }
}

function Ways({ nodes }: { nodes: OutlineNode[] }) {
  if (nodes.length === 0) {
    return <p className="m-0 text-detail text-muted-foreground">A flow itt véget ér.</p>;
  }
  return (
    <ol className="m-0 flex list-none flex-col gap-2 p-0">
      {nodes.map((node) => (
        <li key={`${node.kind}-${node.id}`}>
          <Node node={node} />
        </li>
      ))}
    </ol>
  );
}

function Node({ node }: { node: OutlineNode }) {
  if (node.kind === "goto") {
    return (
      <p className="m-0 text-detail text-muted-foreground">
        → tovább a <span className="data-mono">#{node.id}</span> lépéssel (fent)
      </p>
    );
  }
  if (node.kind === "step") return <Step id={node.id} step={node.step} />;
  if (node.kind === "branches") return <Branches node={node} />;
  const step = node.step;
  const condition = step.kind === "split" ? step.condition : null;
  return (
    <div className="flex flex-col gap-2">
      {condition === null ? (
        <Step id={node.id} step={step} />
      ) : (
        <StepCard
          tone={isUnsupported(condition) ? "danger" : undefined}
          title={
            <>
              Feltételes ág <span className="data-mono text-label text-muted-foreground">#{node.id}</span>
              <StepShadowBadge id={node.id} />
            </>
          }
        >
          {isUnsupported(condition) ? (
            <Unsupported what="feltétel" value={condition} />
          ) : (
            <Condition groups={conditionGroups(condition)} />
          )}
        </StepCard>
      )}
      <div className="ml-5 flex flex-col gap-2 border-l border-line pl-4">
        <p className="m-0 text-label font-semibold text-green-ink">Igen</p>
        <Ways nodes={node.yes} />
        <p className="m-0 mt-1 text-label font-semibold text-red-ink">Nem</p>
        <Ways nodes={node.no} />
      </div>
    </div>
  );
}

/** Klaviyo's multi-branch split: its ways in the order they are tried, each
 * with its name and condition, the else marked; each way's steps under it. */
function Branches({ node }: { node: Extract<OutlineNode, { kind: "branches" }> }) {
  const name = node.step.kind === "multi_split" ? node.step.name : null;
  return (
    <div className="flex flex-col gap-2">
      <StepCard
        title={
          <>
            Többágú elágazás{name ? `: ${name}` : ""}{" "}
            <span className="data-mono text-label text-muted-foreground">#{node.id}</span>
            <StepShadowBadge id={node.id} />
          </>
        }
      >
        <span>Az első ág, amelynek a feltétele igaz; ha egyiké sem, az „Egyéb” ág. A Klaviyóban szerkeszthető.</span>
      </StepCard>
      <div className="ml-5 flex flex-col gap-2 border-l border-line pl-4">
        {node.ways.map((way, index) => (
          <div key={way.id} className="flex flex-col gap-1">
            <p className="m-0 text-label font-semibold text-foreground">
              {way.isElse ? "Egyéb" : `${index + 1}. ${way.name ?? "ág"}`}{" "}
              <span className="data-mono font-normal text-muted-foreground">#{way.id}</span>
            </p>
            {way.condition !== null &&
              (isUnsupported(way.condition) ? (
                <Unsupported what="feltétel" value={way.condition} />
              ) : (
                <Condition groups={conditionGroups(way.condition)} />
              ))}
            <Ways nodes={way.nodes} />
          </div>
        ))}
      </div>
    </div>
  );
}

function Condition({ groups }: { groups: string[][] }) {
  return (
    <ul className="m-0 flex list-none flex-col gap-0.5 p-0 text-foreground">
      {groups.map((group, index) => (
        <li key={index}>
          {index > 0 && <span className="text-muted-foreground">és </span>}
          {group.join(" vagy ")}
        </li>
      ))}
    </ul>
  );
}

export function Definition({ definition, sourceName }: { definition: FlowDefinition; sourceName: string | null }) {
  const drawn = outline(definition);
  const filter = definition.filter === null ? null : definition.filter;
  const reentry = reentrySentence(definition.reentry);
  const extra = Object.entries(definition.extra ?? {});
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Indító és flow-szűrő</CardTitle>
          <CardDescription>A flow-szűrő belépéskor és minden lépés előtt újra számít.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {isUnsupported(definition.trigger) ? (
            <Unsupported what="indító" value={definition.trigger} />
          ) : (
            <p className="m-0">{triggerSentence(definition.trigger, sourceName)}</p>
          )}
          {filter === null ? (
            <p className="m-0 text-muted-foreground">Nincs flow-szűrő.</p>
          ) : isUnsupported(filter) ? (
            <Unsupported what="flow-szűrő" value={filter} />
          ) : (
            <div>
              <p className="m-0 font-medium">Csak az marad a flow-ban, akire igaz:</p>
              <Condition groups={conditionGroups(filter)} />
            </div>
          )}
          {reentry && <p className="m-0">{reentry}</p>}
          {extra.map(([key, value]) => (
            <Unsupported key={key} what={`mező (${key})`} value={value} />
          ))}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Lépések</CardTitle>
          <CardDescription>Sorrendben; a feltételes ág két útja behúzva, a találkozásuk után közös.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <Ways nodes={drawn.nodes} />
          {drawn.unreachable.length > 0 && (
            <div className="flex flex-col gap-2">
              <Callout tone="warn">Ezekhez a lépésekhez a belépésből nem vezet út:</Callout>
              {drawn.unreachable.map((id) => {
                const step = definition.steps[id];
                return step === undefined ? null : <Step key={id} id={id} step={step} />;
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Problems({ version }: { version: FlowVersionDetail }) {
  if (version.unsupported.length === 0) return null;
  return (
    <div role="alert" className="mb-4 rounded-inner bg-red-soft px-4 py-3">
      <p className="m-0 font-semibold text-red-ink">
        Ez a verzió nem futhat árnyékban sem: {version.unsupported.length} helyen van benne olyan, amit nem
        ismerünk.
      </p>
      <ul className="m-0 mt-1 pl-5">
        {version.unsupported.map((problem, index) => (
          <li key={index}>
            <span className="data-mono">{problem.path}</span>: {problem.reason}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Facts({ flow, version }: { flow: FlowDetailData; version: FlowVersionDetail | null }) {
  return (
    <DetailList
      columns={2}
      className="mb-4"
      items={[
        { label: "Ország", value: <Country code={flow.country} /> },
        { label: "Klaviyo-azonosító", value: flow.flow_external_id ?? "—", mono: true },
        {
          label: "Klaviyo-állapot",
          value: flow.source_status ? <StatusPill domain="flowSource" state={flow.source_status} /> : "—",
        },
        { label: "Mód", value: <StatusPill domain="flowMode" state={flow.mode} /> },
        {
          label: "Követi a Klaviyót",
          value: flow.follows_klaviyo
            ? "igen: egy Klaviyo-változás új verzió lesz"
            : "nem: egy Klaviyo-változást csak feljegyzünk",
        },
        { label: "Változás", value: <ChangeMark row={flow} /> },
        {
          label: "Érvényes verzió",
          value: version ? `${version.number}. (${flow.versions} közül)` : "—",
        },
        { label: "Támogatott", value: <SupportMark supported={flow.supported} problems={flow.problems} /> },
      ]}
    />
  );
}

export default function FlowDetail() {
  const params = useParams();
  const id = Number(params.id);
  const navigate = useNavigate();
  const isAdmin = useAuth().user?.role === "admin";
  const [picked, setPicked] = useState<number | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [flowState, reloadFlow] = useLoad(() => fetchFlow(id), [id]);
  const [versionsState] = useLoad(() => fetchFlowVersions(id), [id]);
  const [pickedState] = useLoad(
    () => (picked === null ? Promise.resolve(null) : fetchFlowVersion(id, picked)),
    [id, picked],
  );

  const flow = flowState.data;
  if (flowState.error !== null && flow === null) {
    return <ErrorState message={flowState.error} onRetry={reloadFlow} />;
  }
  if (flow === null) return <LoadingState />;

  const shown = picked === null ? flow.version : pickedState.data;
  const versions = versionsState.data ?? [];

  async function edit() {
    if (flow === null) return;
    if (flow.draft_version !== null) {
      navigate(`/flowk/${id}/vaszon/${flow.draft_version}`);
      return;
    }
    try {
      const draft = await startFlowDraft(id, { from_number: flow.current_version });
      navigate(`/flowk/${id}/vaszon/${draft.number}`);
    } catch (error: unknown) {
      const detail = error instanceof ApiError ? (error.detail as { number?: number } | null) : null;
      if (detail?.number) navigate(`/flowk/${id}/vaszon/${detail.number}`);
      else setEditError(error instanceof Error ? error.message : "Ismeretlen hiba.");
    }
  }

  return (
    <FlowShadow flow={flow} onChanged={reloadFlow}>
      <PageHeader
        back={{ to: "/flowk", label: "Flow-k" }}
        title={flow.name}
        meta={<StatusPill domain="flowMode" state={flow.mode} />}
        description={
          flow.origin === "klaviyo"
            ? "A Klaviyo-flow lépésről lépésre, ahogy az érvényes verzió tartja. A vásznon szerkeszthető; a verziók egymás mellett összevethetők."
            : "Saját flow lépésről lépésre. A vásznon szerkeszthető; a verziók egymás mellett összevethetők."
        }
        actions={
          <>
            {flow.mode === "shadow" && (
              <Button asChild variant="outline" size="sm">
                <Link to={`/flowk/${id}/osszevetes`}>
                  <ScaleIcon />
                  Összevetés
                </Link>
              </Button>
            )}
            <Button asChild variant="outline" size="sm">
              <Link to={`/flowk/${id}/verziok`}>
                <GitCompareArrowsIcon />
                Verziók
              </Link>
            </Button>
            {flow.current_version !== null && (
              <Button asChild variant="outline" size="sm">
                <Link to={`/flowk/${id}/vaszon`}>
                  <WorkflowIcon />
                  Vászon
                </Link>
              </Button>
            )}
            {isAdmin && (
              <Button size="sm" onClick={() => void edit()}>
                <PencilIcon />
                {flow.draft_version !== null ? `Vázlat folytatása (${flow.draft_version}.)` : "Szerkesztés"}
              </Button>
            )}
          </>
        }
      />
      {editError && <ErrorState message={editError} className="mb-4" />}
      <Facts flow={flow} version={flow.version} />
      <FlowShadowCard />
      {versions.length > 1 && (
        <Field label="Verzió" htmlFor="flow-version" className="mb-4 max-w-120">
          <NativeSelect
            id="flow-version"
            value={picked === null ? "" : String(picked)}
            onChange={(event) => setPicked(event.target.value === "" ? null : Number(event.target.value))}
          >
            <option value="">Az érvényes ({flow.current_version}.)</option>
            {versions.map((version) => (
              <option key={version.number} value={version.number}>
                {version.number}. · {labelOfVersion(version.state)} · {minute(version.created_at)}
                {version.note ? ` · ${version.note}` : ""}
              </option>
            ))}
          </NativeSelect>
        </Field>
      )}
      {pickedState.error !== null && picked !== null && <ErrorState message={pickedState.error} className="mb-4" />}
      {shown === null || shown === undefined ? (
        picked !== null && pickedState.error === null ? (
          <LoadingState />
        ) : (
          <EmptyState
            title="Nincs érvényes verzió"
            description={
              flow.origin === "emc"
                ? "A flow vázlata a vásznon szerkeszthető; közzététel után ez lesz az érvényes verzió."
                : "A leltár még nem olvasta be ezt a flow-t."
            }
          />
        )
      ) : (
        <>
          {picked !== null && (
            <Callout className="mb-4">
              A {shown.number}. verziót nézed ({labelOfVersion(shown.state)}, {minute(shown.created_at)}).
            </Callout>
          )}
          <Problems version={shown} />
          <FlowTemplatesProvider country={flow.country}>
            <Definition definition={shown.definition} sourceName={flow.trigger?.source_name ?? null} />
          </FlowTemplatesProvider>
        </>
      )}
    </FlowShadow>
  );
}

function labelOfVersion(state: string): string {
  return statusLook("flowVersion", state).label;
}
